import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { RevealLines, EASE } from "../site/motion";
import { PhoneFrame, TabletFrame } from "./Devices";

const Phones = ({ imgs, progress }) => {
  const y0 = useTransform(progress, [0, 1], ["0%", "-18%"]);
  const y1 = useTransform(progress, [0, 1], ["0%", "-34%"]);
  const y2 = useTransform(progress, [0, 1], ["0%", "-8%"]);
  const ys = [y0, y1, y2];
  const pose = [
    { x: "-62%", r: -9, z: 1, d: 0.15 },
    { x: "0%", r: 0, z: 3, d: 0 },
    { x: "62%", r: 9, z: 2, d: 0.25 },
  ];
  return (
    <div className="relative mx-auto h-[360px] w-full max-w-[640px] sm:h-[560px]">
      {imgs.map((src, i) => (
        <motion.div key={src} style={{ y: ys[i], zIndex: pose[i].z }} className="absolute left-1/2 top-6 w-[150px] -translate-x-1/2 sm:w-[230px]">
          <motion.div
            initial={{ opacity: 0, y: 120, rotate: 0, x: "0%" }}
            animate={{ opacity: 1, y: 0, rotate: pose[i].r, x: pose[i].x }}
            transition={{ duration: 1.4, ease: EASE, delay: 1.1 + pose[i].d }}
          >
            <PhoneFrame src={src} alt="Junction app screen" />
          </motion.div>
        </motion.div>
      ))}
    </div>
  );
};

const Tablet = ({ src, caption, progress }) => {
  const rotateX = useTransform(progress, [0, 0.6], [28, 0]);
  const scale = useTransform(progress, [0, 0.6], [0.92, 1]);
  return (
    <div className="mx-auto max-w-5xl [perspective:1600px]">
      <motion.div style={{ rotateX, scale }} initial={{ opacity: 0, y: 80 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.4, ease: EASE, delay: 1.1 }} className="origin-bottom">
        <TabletFrame src={src} alt="Gamesense court tablet" />
      </motion.div>
      {caption && <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.2em] text-cream/60">{caption}</p>}
    </div>
  );
};

export const CaseHero = ({ c }) => {
  const ref = useRef(null);
  const visual = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const { scrollYProgress: vp } = useScroll({ target: visual, offset: ["start end", "end start"] });
  const titleY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  return (
    <section ref={ref} data-testid="case-hero" className="relative overflow-hidden pt-32 md:pt-40">
      <div className="hex-pattern pointer-events-none absolute inset-0 opacity-50" />
      <div className="case-glow pointer-events-none absolute inset-0" />
      <div className="relative mx-auto max-w-[1440px] px-6 md:px-10">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 }} className="flex flex-wrap items-center justify-between gap-4">
          <Link to="/#work" data-testid="case-back-link" className="group flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-cream/70 hover:text-cream">
            <ArrowLeft className="h-3.5 w-3.5 transition-transform group-hover:-translate-x-1" /> All work
          </Link>
          <span className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-cream/70">
            <span className="h-2 w-2 rounded-full" style={{ background: "var(--case)" }} />{c.index} · {c.kicker}
          </span>
        </motion.div>
        <motion.div style={{ y: titleY }}>
          <RevealLines lines={[c.name]} delay={0.75}
            className="mt-10 font-display text-[18vw] font-semibold uppercase leading-[0.82] tracking-[-0.05em] text-cream lg:text-[13rem]" />
        </motion.div>
        <div className="mt-10 grid gap-10 lg:grid-cols-12">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2, duration: 0.9, ease: EASE }}
            className="font-display text-2xl font-medium leading-snug text-cream md:text-3xl lg:col-span-6">{c.tagline}</motion.p>
          <motion.dl initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.35, duration: 0.9, ease: EASE }}
            className="grid grid-cols-2 gap-px self-start overflow-hidden rounded-2xl border border-cream/10 bg-cream/10 lg:col-span-5 lg:col-start-8">
            {c.meta.map((m) => (
              <div key={m.k} data-testid={`case-meta-${m.k.toLowerCase()}`} className="bg-skaris-ink p-5">
                <dt className="font-mono text-[11px] uppercase tracking-[0.2em] text-cream/55">{m.k}</dt>
                <dd className="mt-2 font-body text-sm text-cream">{m.v}</dd>
              </div>
            ))}
          </motion.dl>
        </div>
        <div ref={visual} className="mt-16 md:mt-24">
          {c.hero.type === "phones" ? <Phones imgs={c.hero.imgs} progress={vp} /> : <Tablet src={c.hero.imgs[0]} caption={c.hero.caption} progress={vp} />}
        </div>
      </div>
    </section>
  );
};
